import React, { useEffect, useState, useMemo } from "react";
import { DataCorrectionTable } from "./DataCorrectionPage";
import {
  dataCorrection,
  getDataCorrectionRequests,
  downloadCorrectionFile,
  fetchIndustries,
  fetchIndustryFields,
  getCorrectionFieldKeys,
} from "../api";
import { useTheme } from "../context/ThemeContext";

function parseCSVLine(line) {
  const result = [];
  let startValueIndex = 0;
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '"') {
      inQuotes = !inQuotes;
    } else if (line[i] === "," && !inQuotes) {
      let val = line.substring(startValueIndex, i).trim();
      if (val.startsWith('"') && val.endsWith('"')) {
        val = val.slice(1, -1).replace(/""/g, '"');
      }
      result.push(val);
      startValueIndex = i + 1;
    }
  }
  let val = line.substring(startValueIndex).trim();
  if (val.startsWith('"') && val.endsWith('"')) {
    val = val.slice(1, -1).replace(/""/g, '"');
  }
  result.push(val);
  return result;
}

function parseCsvHeaderPairs(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject("Please choose a CSV file.");
      return;
    }
    if (file.size === 0) {
      reject("CSV file is empty.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result || "";
      const trimmed = text.trim();
      if (!trimmed) {
        reject("CSV file is empty.");
        return;
      }
      const lines = trimmed.split(/\r?\n/);
      const rawHeaders = parseCSVLine(lines[0]);
      const byNorm = new Map();
      rawHeaders.forEach((raw) => {
        const norm = raw.trim().toLowerCase().replace(/\s+/g, "_");
        if (!norm) return;
        if (!byNorm.has(norm)) {
          byNorm.set(norm, raw.trim() || norm);
        }
      });
      const pairs = [...byNorm.entries()].map(([norm, display]) => ({
        norm,
        display: display || norm,
      }));
      if (pairs.length === 0) {
        reject("CSV has no usable column headers.");
        return;
      }
      resolve(pairs);
    };
    reader.onerror = () => reject("Could not read the CSV file.");
    reader.readAsText(file);
  });
}

function suggestTarget(norm, correctionSet, industrySet, isEnrichment) {
  if (correctionSet.has(norm)) return norm;
  if (isEnrichment && industrySet.has(norm)) return norm;
  const n = norm.toLowerCase().replace(/-/g, "_");

  // Common CSV header aliases → standard correction keys
  if (n === "company_name" || n === "companyname") {
    return "company_name";
  }
  if (n === "company_country" || n === "companycountry") {
    return "hq_country";
  }
  if (
    n === "website" ||
    n === "company_website" ||
    n === "companywebsite"
  ) {
    return "website";
  }
  // Do not auto-map companyEmployeeCount → employee_size_range (exact vs range semantics).
  const skipEmployeeSizeRangeAuto =
    n === "companyemployeecount" || n === "company_employee_count";

  if (
    !skipEmployeeSizeRangeAuto &&
    (n === "employee_size_range" ||
      n === "company_headcount" ||
      n === "companyheadcount" ||
      n.includes("headcount") ||
      (n.includes("employee") && n.includes("count")))
  ) {
    return "employee_size_range";
  }

  if (n === "linkedin_url" || n === "linkedin") return "linkedin_url";
  if (
    n.includes("linkedin") &&
    (n.includes("url") ||
      n.includes("profile") ||
      n.includes("link") ||
      n.endsWith("_in"))
  ) {
    return "linkedin_url";
  }
  if (
    n === "company_linkedin" ||
    n === "companylinkedin" ||
    n === "company_linkedin_url"
  ) {
    return "linkedin_url";
  }
  return "";
}

function buildInitialTargets(pairs, correctionKeys, industryFields, isEnrichment) {
  const correctionSet = new Set(correctionKeys);
  const industrySet = new Set(industryFields);
  const next = {};
  pairs.forEach(({ norm }) => {
    next[norm] = suggestTarget(norm, correctionSet, industrySet, isEnrichment);
  });
  return next;
}

function labelForCorrectionKey(k) {
  if (k === "linkedin_url") return "LinkedIn URL (required)";
  return k
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function DataCorrectionUploader({ onViewLogs }) {
  const { theme } = useTheme();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);

  const [isEnrichment, setIsEnrichment] = useState(false);
  const [industry, setIndustry] = useState("");
  const [isNewIndustry, setIsNewIndustry] = useState(false);
  const [industryList, setIndustryList] = useState([]);

  const [correctionKeys, setCorrectionKeys] = useState([]);
  const [industryFields, setIndustryFields] = useState([]);

  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [csvHeaderPairs, setCsvHeaderPairs] = useState([]);
  const [columnTargets, setColumnTargets] = useState({});
  const [modalError, setModalError] = useState(null);

  const loadHistory = async () => {
    try {
      const res = await getDataCorrectionRequests();
      setHistory(res.data || []);
    } catch (err) {
      console.error("Failed to load history", err);
      setMessage("Failed to load upload history.");
      setMessageType("error");
    }
  };

  const loadIndustries = async () => {
    try {
      const res = await fetchIndustries();
      setIndustryList(res.data.industries || []);
    } catch (err) {
      console.warn("Failed to load industries", err);
      setIndustryList([]);
    }
  };

  const loadCorrectionKeys = async () => {
    try {
      const res = await getCorrectionFieldKeys();
      setCorrectionKeys(res.data.keys || []);
    } catch (err) {
      console.warn("Failed to load correction field keys", err);
      setCorrectionKeys([]);
    }
  };

  useEffect(() => {
    loadHistory();
    loadIndustries();
    loadCorrectionKeys();
  }, []);

  useEffect(() => {
    if (!isEnrichment || !industry.trim()) {
      setIndustryFields([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchIndustryFields(industry.trim());
        if (!cancelled) setIndustryFields(res.data.fields || []);
      } catch (e) {
        if (!cancelled) setIndustryFields([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEnrichment, industry]);

  const standardTargetSet = useMemo(() => new Set(correctionKeys), [correctionKeys]);

  const openMappingModal = async () => {
    setMessage(null);
    setMessageType(null);
    setModalError(null);
    if (!file) {
      setMessage("Please choose a CSV file first.");
      setMessageType("error");
      return;
    }
    if (isEnrichment && !industry.trim()) {
      setMessage("Please select an industry for enrichment uploads.");
      setMessageType("error");
      return;
    }
    if (!correctionKeys.length) {
      setMessage("Could not load standard field list. Refresh and try again.");
      setMessageType("error");
      return;
    }
    try {
      const pairs = await parseCsvHeaderPairs(file);
      setCsvHeaderPairs(pairs);
      setColumnTargets(
        buildInitialTargets(pairs, correctionKeys, industryFields, isEnrichment)
      );
      setMappingModalOpen(true);
    } catch (msg) {
      setMessage(msg);
      setMessageType("error");
    }
  };

  const setTargetForNorm = (norm, value) => {
    setColumnTargets((prev) => ({ ...prev, [norm]: value }));
  };

  const buildFieldMappingsPayload = () => {
    const field_mappings = {};
    csvHeaderPairs.forEach(({ norm }) => {
      const t = (columnTargets[norm] || "").trim();
      if (t) field_mappings[norm] = t;
    });
    return field_mappings;
  };

  const validateMappingsClient = (field_mappings) => {
    const vals = Object.values(field_mappings).filter(Boolean);
    if (!vals.includes("linkedin_url")) {
      return "Map exactly one CSV column to LinkedIn URL (linkedin_url).";
    }
    if (vals.filter((v) => v === "linkedin_url").length !== 1) {
      return "Only one column may map to linkedin_url.";
    }
    if (isEnrichment) {
      const hasNonStandard = vals.some((v) => !standardTargetSet.has(v));
      if (!hasNonStandard) {
        return "Enrichment uploads need at least one column mapped to an industry-specific or new custom field.";
      }
    }
    return null;
  };

  const handleConfirmUpload = async () => {
    setModalError(null);
    const field_mappings = buildFieldMappingsPayload();
    const err = validateMappingsClient(field_mappings);
    if (err) {
      setModalError(err);
      return;
    }

    const formData = new FormData();
    formData.append("csvFile", file);
    formData.append("isEnrichment", isEnrichment);
    formData.append("field_mappings", JSON.stringify(field_mappings));
    if (isEnrichment) {
      formData.append("industry", industry.trim());
      formData.append("isNewIndustry", isNewIndustry);
    }

    setUploading(true);
    try {
      await dataCorrection(formData);
      setMappingModalOpen(false);
      setFile(null);
      setIsEnrichment(false);
      setIndustry("");
      setIsNewIndustry(false);
      setCsvHeaderPairs([]);
      setColumnTargets({});
      await loadHistory();
      await loadIndustries();
      setMessage("File uploaded for approval.");
      setMessageType("success");
    } catch (err) {
      console.error("Upload failed", err);
      const backendMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Upload failed. Please try again.";
      setModalError(backendMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (requestId) => {
    try {
      const res = await downloadCorrectionFile(requestId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${requestId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
      setMessage("Failed to download CSV.");
      setMessageType("error");
    }
  };

  return (
    <div style={{ padding: "30px", maxWidth: "1000px" }}>
      <h2 style={{ marginBottom: "20px" }}>Upload Corrections</h2>

      {message && (
        <div
          style={{
            marginBottom: "15px",
            padding: "10px 14px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
            background:
              messageType === "success" ? "#e8f8f0" : "#fdecea",
            border:
              messageType === "success"
                ? "1px solid #c8e6d9"
                : "1px solid #f5c2c7",
            color: messageType === "success" ? "#148f50" : "#b71c1c",
          }}
        >
          {message}
        </div>
      )}

      <div
        style={{
          background: "#fff",
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          marginBottom: "30px",
        }}
      >
        <label style={{ fontSize: "14px", fontWeight: 600 }}>
          CSV file
        </label>
        <p style={{ fontSize: "12px", color: "#6b7280", margin: "6px 0 0 0" }}>
          After upload you will map columns. At least one column must map to{" "}
          <code>linkedin_url</code>.
        </p>
        <input
          type="file"
          accept=".csv"
          onChange={(e) => setFile(e.target.files[0] || null)}
          style={{
            display: "block",
            marginTop: "10px",
            padding: "8px",
            border: "1px solid #ccc",
            borderRadius: "6px",
          }}
        />

        <div style={{ marginTop: "15px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            <input
              type="checkbox"
              checked={isEnrichment}
              onChange={(e) => {
                setIsEnrichment(e.target.checked);
                if (!e.target.checked) {
                  setIndustry("");
                  setIsNewIndustry(false);
                }
              }}
              style={{ marginRight: "8px" }}
            />
            Enrichment data (industry-specific)
          </label>
          <div
            style={{
              marginLeft: "24px",
              marginTop: "4px",
              fontSize: "12px",
              color: "#6b7280",
            }}
          >
            Adds industry custom fields to the mapping list and allows new custom
            field names.
          </div>
        </div>

        {isEnrichment && (
          <div style={{ marginTop: "10px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: 600,
                marginBottom: "5px",
              }}
            >
              Industry <span style={{ color: "red" }}>*</span>
            </label>
            {industryList.length > 0 && (
              <input
                id="industry-combo-input"
                list="industry-options"
                value={industry}
                onChange={(e) => {
                  const val = e.target.value;
                  setIndustry(val);
                  const matched = industryList.some(
                    (ind) => ind.toLowerCase() === val.toLowerCase()
                  );
                  setIsNewIndustry(Boolean(val) && !matched);
                }}
                placeholder="Search or type an industry…"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                }}
              />
            )}
            {industryList.length > 0 && (
              <datalist id="industry-options">
                {industryList.map((ind, idx) => (
                  <option key={idx} value={ind} />
                ))}
              </datalist>
            )}
            {industryList.length === 0 && (
              <input
                id="industry-combo-input"
                type="text"
                value={industry}
                onChange={(e) => {
                  const v = e.target.value;
                  setIndustry(v);
                  setIsNewIndustry(Boolean(v.trim()));
                }}
                placeholder="e.g. hotel, technology..."
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                }}
              />
            )}
          </div>
        )}

        <button
          disabled={uploading}
          onClick={openMappingModal}
          style={{
            marginTop: "15px",
            padding: "10px 18px",
            background: uploading ? "#95a5a6" : "#3498db",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: uploading ? "not-allowed" : "pointer",
            fontWeight: 600,
          }}
        >
          Map columns &amp; submit
        </button>
      </div>

      <h3 style={{ marginBottom: "10px" }}>My Upload History</h3>

      <DataCorrectionTable
        requests={history}
        loading={false}
        isRevertedTab={false}
        canApprove={false}
        onDownload={handleDownload}
        onApprove={() => {}}
        onReject={() => {}}
        onViewLogs={onViewLogs}
      />

      {mappingModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: theme.cardBackground,
              padding: "24px",
              borderRadius: "8px",
              width: "720px",
              maxWidth: "95%",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${theme.border}`,
            }}
          >
            <h3 style={{ margin: 0, color: theme.text }}>
              Map CSV columns
            </h3>
            <p
              style={{
                fontSize: "13px",
                color: theme.textMuted,
                margin: "8px 0 12px 0",
              }}
            >
              {isEnrichment
                ? `Industry: ${industry || "(none)"} — type to filter suggestions or enter a custom target field name. Leave a column blank to skip it.`
                : "Standard correction — type to filter suggestions; only standard fields are allowed. Leave a column blank to skip it."}
            </p>
            {isEnrichment && (
              <p
                style={{
                  fontSize: "12px",
                  color: theme.textMuted,
                  margin: "0 0 12px 0",
                }}
              >
                Custom fields: you can type a target name that is not in the list; it
                is validated when you submit.
              </p>
            )}

            {modalError && (
              <div
                style={{
                  padding: "10px 12px",
                  marginBottom: "12px",
                  borderRadius: "6px",
                  background: "#fdecea",
                  color: "#b71c1c",
                  fontSize: "13px",
                }}
              >
                {modalError}
              </div>
            )}

            <div
              style={{
                overflowY: "auto",
                flex: 1,
                marginBottom: "16px",
                border: `1px solid ${theme.border}`,
                borderRadius: "6px",
              }}
            >
              <table
                width="100%"
                cellPadding={8}
                style={{ borderCollapse: "collapse", fontSize: "13px" }}
              >
                <thead
                  style={{
                    background: theme.tableHeader,
                    position: "sticky",
                    top: 0,
                  }}
                >
                  <tr>
                    <th align="left" style={{ color: theme.textSecondary }}>
                      CSV column
                    </th>
                    <th align="left" style={{ color: theme.textSecondary }}>
                      Maps to
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {csvHeaderPairs.map(({ norm, display }, rowIdx) => (
                    <tr
                      key={norm}
                      style={{ borderTop: `1px solid ${theme.borderLight}` }}
                    >
                      <td style={{ color: theme.text, fontWeight: 500 }}>
                        {display}
                        <div
                          style={{
                            fontSize: "11px",
                            color: theme.textMuted,
                            fontWeight: 400,
                          }}
                        >
                          {norm}
                        </div>
                      </td>
                      <td>
                        <input
                          list={`correction-map-datalist-${rowIdx}`}
                          value={columnTargets[norm] ?? ""}
                          onChange={(e) =>
                            setTargetForNorm(norm, e.target.value)
                          }
                          placeholder="Type or leave blank to skip…"
                          autoComplete="off"
                          style={{
                            width: "100%",
                            maxWidth: "400px",
                            padding: "8px",
                            borderRadius: "6px",
                            border: `1px solid ${theme.inputBorder}`,
                            background: theme.inputBackground,
                            color: theme.text,
                            fontSize: "13px",
                          }}
                        />
                        <datalist id={`correction-map-datalist-${rowIdx}`}>
                          {correctionKeys.map((k) => (
                            <option
                              key={k}
                              value={k}
                              label={labelForCorrectionKey(k)}
                            />
                          ))}
                          {isEnrichment &&
                            industryFields
                              .filter((k) => !correctionKeys.includes(k))
                              .map((k) => (
                                <option
                                  key={k}
                                  value={k}
                                  label={`${k} (industry)`}
                                />
                              ))}
                        </datalist>
                        {isEnrichment && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: theme.textMuted,
                              marginTop: "4px",
                            }}
                          >
                            Enrichment: you can type a new custom field name not in
                            the list.
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                type="button"
                disabled={uploading}
                onClick={() => {
                  setMappingModalOpen(false);
                  setModalError(null);
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: `1px solid ${theme.border}`,
                  background: theme.cardBackground,
                  cursor: uploading ? "not-allowed" : "pointer",
                  color: theme.text,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={uploading}
                onClick={handleConfirmUpload}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  background: theme.primary,
                  color: "#fff",
                  cursor: uploading ? "not-allowed" : "pointer",
                  fontWeight: 600,
                }}
              >
                {uploading ? "Uploading…" : "Submit for approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
